/**
 * The API speaks RFC 3339 UTC instants ("2026-10-05T14:03:00Z") and
 * business dates ("2026-10-31"). Instants render in the browser's locale
 * and time zone. Business dates are calendar dates and must not shift with
 * the viewer's zone, so they are formatted as UTC calendar dates.
 */

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export type InstantStyle = 'date' | 'datetime' | 'time' | 'full' | 'month';

const instantFormats: Record<InstantStyle, Intl.DateTimeFormatOptions> = {
  date: { year: 'numeric', month: 'short', day: 'numeric' },
  datetime: { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
  time: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
  full: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  },
  month: { year: 'numeric', month: 'short' },
};

const dtfCache = new Map<string, Intl.DateTimeFormat>();

function dtf(options: Intl.DateTimeFormatOptions, locale?: string): Intl.DateTimeFormat {
  const key = `${locale ?? ''}|${JSON.stringify(options)}`;
  let f = dtfCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    dtfCache.set(key, f);
  }
  return f;
}

/*
 * Instants render in the display zone and clock: the tenant's zone unless
 * the viewer chose another (app/preferences.ts), never the browser's by
 * accident. Set once per session render, like the home currency.
 */
let displayTimeZone: string | undefined;
let displayHour12: boolean | undefined;

export function setDisplayPreferences(prefs: {
  timeZone?: string | undefined;
  hour12?: boolean | undefined;
}): void {
  displayTimeZone = prefs.timeZone;
  displayHour12 = prefs.hour12;
}

export function getDisplayTimeZone(): string | undefined {
  return displayTimeZone;
}

export function parseInstant(iso: string): Date | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatInstant(
  iso: string | null | undefined,
  style: InstantStyle = 'datetime',
  options: { locale?: string; timeZone?: string } = {},
): string {
  if (!iso) return '—';
  const date = parseInstant(iso);
  if (!date) return iso;
  const timeZone = options.timeZone ?? displayTimeZone;
  const clock =
    displayHour12 !== undefined && style !== 'date' && style !== 'month' ? { hour12: displayHour12 } : {};
  return dtf(
    { ...instantFormats[style], ...(timeZone ? { timeZone } : {}), ...clock },
    options.locale,
  ).format(date);
}

export function parseYmd(ymd: string): { year: number; month: number; day: number } | null {
  const m = YMD_RE.exec(ymd);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

export type DateStyle = 'short' | 'medium' | 'long' | 'month';

const dateFormats: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { year: '2-digit', month: 'numeric', day: 'numeric', timeZone: 'UTC' },
  medium: { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' },
  long: { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' },
  month: { year: 'numeric', month: 'short', timeZone: 'UTC' },
};

/** Format a YYYY-MM-DD business date without time-zone drift. */
export function formatDate(
  ymd: string | null | undefined,
  style: DateStyle = 'medium',
  locale?: string,
): string {
  if (!ymd) return '—';
  const parts = parseYmd(ymd.slice(0, 10));
  if (!parts) return ymd;
  return dtf(dateFormats[style], locale).format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)));
}

/** "2026-10" → "Oct 2026" */
export function formatMonth(ym: string, locale?: string): string {
  return formatDate(ym.length === 7 ? `${ym}-01` : ym, 'month', locale);
}

const rtfCache = new Map<string, Intl.RelativeTimeFormat>();

export function formatRelative(
  iso: string | null | undefined,
  now: Date = new Date(),
  locale?: string,
): string {
  if (!iso) return '—';
  const date = parseInstant(iso);
  if (!date) return iso;
  const key = locale ?? '';
  let rtf = rtfCache.get(key);
  if (!rtf) {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    rtfCache.set(key, rtf);
  }
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 45) return rtf.format(0, 'second');
  if (abs < 45 * 60) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 22 * 3600) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 26 * 86400) return rtf.format(Math.round(seconds / 86400), 'day');
  if (abs < 320 * 86400) return rtf.format(Math.round(seconds / (30 * 86400)), 'month');
  return rtf.format(Math.round(seconds / (365 * 86400)), 'year');
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Today's calendar date in the given IANA zone (default: the display zone). */
export function todayYmd(timeZone?: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone ?? displayTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function addDaysYmd(ymd: string, days: number): string {
  const parts = parseYmd(ymd);
  if (!parts) return ymd;
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Whole days from `from` to `to` (both YYYY-MM-DD); negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  const a = parseYmd(from);
  const b = parseYmd(to);
  if (!a || !b) return 0;
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000,
  );
}

/** RFC 3339 UTC without milliseconds, as the API emits. */
export function toRfc3339(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** A local <input type="datetime-local"> value → RFC 3339 UTC. */
export function localDateTimeToRfc3339(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : toRfc3339(date);
}

/** Minutes the zone is ahead of UTC at an instant (Toronto in summer: -240). */
function zoneOffsetMinutes(utcMillis: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMillis));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return Math.round((asUtc - utcMillis) / 60_000);
}

/** Midnight at the start of a calendar date in an IANA zone, as an RFC 3339 instant. */
export function startOfDayInZone(ymd: string, timeZone: string): string {
  const p = parseYmd(ymd);
  if (!p) return ymd;
  const wall = Date.UTC(p.year, p.month - 1, p.day);
  // Two passes settle the offset across a DST change near midnight.
  let utc = wall - zoneOffsetMinutes(wall, timeZone) * 60_000;
  utc = wall - zoneOffsetMinutes(utc, timeZone) * 60_000;
  return toRfc3339(new Date(utc));
}

/** The first day of the month `ymd` falls in, shifted by whole months (YYYY-MM-01). */
export function monthStartYmd(ymd: string, monthsAhead = 0): string {
  const p = parseYmd(ymd);
  if (!p) return ymd;
  const index = p.year * 12 + (p.month - 1) + monthsAhead;
  return `${Math.floor(index / 12)}-${pad((index % 12) + 1)}-01`;
}
